/*
 * cards-center: cart-page (=/cart) UX uitbreidingen.
 *
 * Werkt UITSLUITEND op de /cart pagina (main-cart-items.liquid). Werkt
 * NIET in de cart drawer (sections/form-mini-cart.liquid + minicart.liquid)
 * — daar gelden andere selectors en flow.
 *
 * Functionaliteiten
 * -----------------
 * 1) Sortering
 *    - Dropdown bovenaan items: 'default' (= volgorde van toevoegen,
 *      Shopify cart-order) of 'title' (= alfabetisch op product-titel,
 *      "Set & nummer" voor TCG kaarten).
 *    - DOM-only sort (.cart-item nodes herschikken op data-product-title).
 *    - Keuze onthouden in localStorage (cc_cart_sort).
 *
 * 2) Pagination ("Meer laden")
 *    - Op grote winkelwagens (>50 lijnen): toon eerst 50, verberg de
 *      rest met cc-cart-hidden. Knop reveals 50 erbij per klik.
 *    - Pure DOM, geen extra fetch — items staan al in HTML.
 *    - Helpt initial render-performance bij 100+ items.
 *
 * 3) Hover preview (alleen desktop ≥ 992px)
 *    - Mouseover op een cart-item image → fixed overlay rechts naast
 *      het item met een vergrote versie. Verbergt op mouseleave.
 *
 * Re-init: na elke cart-update (cart.js sectie-replace) wordt de
 * .js-contents container opnieuw gevuld. We luisteren via
 * MutationObserver en re-applyen sort + pagination + hover-binding.
 *
 * Wat NIET aangeraakt wordt
 * -------------------------
 * - Theme cart.js (waar de afreken-flow + refresh-bescherming zit).
 * - <cart-items>, <cart-remove-button>, <quantity-input> custom elements.
 * - qty-limits.js (eigen file voor min/step/max enforcement).
 * - Alle data-attributen + form#cart submit naar /cart.
 */
(function () {
  'use strict';

  const SORT_KEY = 'cc_cart_sort';
  const PAGE_SIZE = 50;
  const DESKTOP_BREAKPOINT = 992;
  const PREVIEW_W = 280;  // hover preview breedte (px)
  const PREVIEW_H = 392;  // ~ 5:7 portrait ratio voor TCG kaarten

  // ===== Helpers =====
  function getRoot() {
    // Outer container van de redesigned cart-pagina
    return document.querySelector('.cc-cart-page');
  }
  function getItemsContainer() {
    // De ul (of tbody) waar de cart-item rijen in zitten
    return document.querySelector('.cc-cart-page .cc-cart-items');
  }
  function getItems() {
    const c = getItemsContainer();
    return c ? Array.from(c.querySelectorAll(':scope > .cart-item')) : [];
  }

  // ===== Sortering =====
  function getSavedSort() {
    try {
      return localStorage.getItem(SORT_KEY) || 'default';
    } catch (e) { return 'default'; }
  }
  function saveSort(v) {
    try { localStorage.setItem(SORT_KEY, v); } catch (e) {}
  }

  function applySort(mode) {
    const items = getItems();
    if (items.length < 2) return;
    if (mode === 'title') {
      items.sort(function (a, b) {
        const at = (a.dataset.productTitle || '').toLowerCase();
        const bt = (b.dataset.productTitle || '').toLowerCase();
        // Use locale compare with numeric option, zodat "001" < "002" < "010"
        return at.localeCompare(bt, 'nl', { numeric: true, sensitivity: 'base' });
      });
    } else {
      // 'default': sorteer op data-cart-index (volgorde van toevoegen)
      items.sort(function (a, b) {
        const ai = parseInt(a.dataset.cartIndex || '0', 10);
        const bi = parseInt(b.dataset.cartIndex || '0', 10);
        return ai - bi;
      });
    }
    const c = getItemsContainer();
    if (!c) return;
    items.forEach(function (el) { c.appendChild(el); });
  }

  function bindSortUI() {
    const select = document.querySelector('.cc-cart-page .cc-cart-sort');
    if (!select || select._ccBound) return;
    const saved = getSavedSort();
    if (saved && Array.from(select.options).some(function (o) { return o.value === saved; })) {
      select.value = saved;
    }
    select.addEventListener('change', function () {
      saveSort(select.value);
      // applyAllSafe doet sort+pagination en suppresst observer-loop
      mutationIgnoreUntil = Date.now() + 100;
      applySort(select.value);
      applyPagination();
    });
    select._ccBound = true;
  }

  // ===== Pagination ("Meer laden") =====
  // visibleCount = aantal items dat zichtbaar is in DOM-volgorde
  let visibleCount = PAGE_SIZE;

  function applyPagination() {
    const items = getItems();
    const total = items.length;
    const footer = document.querySelector('.cc-cart-page .cc-cart-pagination');
    const btn = document.querySelector('.cc-cart-page .cc-cart-load-more');
    const counter = document.querySelector('.cc-cart-page .cc-cart-load-more-count');

    if (total <= PAGE_SIZE) {
      // Niets te pagineren: alles tonen, footer verbergen
      items.forEach(function (el) { el.classList.remove('cc-cart-hidden'); });
      if (footer) footer.hidden = true;
    } else {
      items.forEach(function (el, i) {
        el.classList.toggle('cc-cart-hidden', i >= visibleCount);
      });
      const remaining = total - visibleCount;
      if (footer) footer.hidden = remaining <= 0;
      if (counter) counter.textContent = String(remaining);
    }
    // Loading-strategie:
    // - zichtbare items (i < visibleCount): loading="eager" zodat
    //   alle images direct laden, ook op mobile waar native lazy
    //   met onze 80×112 container + theme aspect-ratio inner-wrapper
    //   onbetrouwbaar bleek (eerste paar laden, rest blijft leeg).
    // - verborgen items (cc-cart-hidden, display:none): loading="lazy"
    //   zodat ze pas laden bij "Meer laden" reveal.
    items.forEach(function (el, i) {
      const visible = i < visibleCount;
      el.querySelectorAll('img').forEach(function (img) {
        img.setAttribute('loading', visible ? 'eager' : 'lazy');
      });
    });
  }

  function bindPaginationUI() {
    const btn = document.querySelector('.cc-cart-page .cc-cart-load-more');
    if (!btn || btn._ccBound) return;
    btn.addEventListener('click', function (e) {
      e.preventDefault();
      visibleCount += PAGE_SIZE;
      applyPagination();
    });
    btn._ccBound = true;
  }

  // ===== Hover preview =====
  let previewEl = null;
  let previewHideTimer = null;

  function ensurePreviewEl() {
    if (previewEl) return previewEl;
    previewEl = document.createElement('div');
    previewEl.className = 'cc-cart-hover-preview';
    previewEl.setAttribute('aria-hidden', 'true');
    previewEl.innerHTML = '<img alt="" />';
    document.body.appendChild(previewEl);
    return previewEl;
  }

  function showPreview(srcUrl, anchorRect) {
    if (window.innerWidth < DESKTOP_BREAKPOINT) return;
    if (!srcUrl) return;
    const el = ensurePreviewEl();
    const img = el.querySelector('img');
    if (img.getAttribute('src') !== srcUrl) img.setAttribute('src', srcUrl);
    // Positioneer rechts van het item, verticaal gecentreerd op anchor
    const padding = 12;
    let left = anchorRect.right + padding;
    let top = anchorRect.top + window.scrollY + anchorRect.height / 2 - PREVIEW_H / 2;
    // Houd binnen viewport rechts: als niet genoeg ruimte, links plaatsen
    if (left + PREVIEW_W > window.innerWidth - padding) {
      left = anchorRect.left - PREVIEW_W - padding;
    }
    // Houd binnen viewport verticaal
    const minTop = window.scrollY + padding;
    const maxTop = window.scrollY + window.innerHeight - PREVIEW_H - padding;
    if (top < minTop) top = minTop;
    if (top > maxTop) top = maxTop;
    el.style.left = left + 'px';
    el.style.top = top + 'px';
    el.style.width = PREVIEW_W + 'px';
    el.style.height = PREVIEW_H + 'px';
    clearTimeout(previewHideTimer);
    el.classList.add('is-visible');
  }

  function hidePreview() {
    if (!previewEl) return;
    clearTimeout(previewHideTimer);
    previewHideTimer = setTimeout(function () {
      if (previewEl) previewEl.classList.remove('is-visible');
    }, 60);
  }

  function bindHoverPreview() {
    const root = getRoot();
    if (!root || root._ccHoverBound) return;
    // Delegated mouseover/mouseout op de items-container
    root.addEventListener('mouseover', function (e) {
      const media = e.target.closest('.cc-cart-page .cart-item__media');
      if (!media) return;
      if (window.innerWidth < DESKTOP_BREAKPOINT) return;
      const img = media.querySelector('img');
      // Probeer de hoogste-resolutie src uit srcset te halen
      let src = img && (img.getAttribute('data-zoom-src') || img.currentSrc || img.src);
      if (img && img.srcset) {
        // Pak de laatste (= grootste) src uit srcset
        const last = img.srcset.split(',').map(function (s) { return s.trim().split(' ')[0]; }).pop();
        if (last) src = last;
      }
      showPreview(src, media.getBoundingClientRect());
    });
    root.addEventListener('mouseout', function (e) {
      const media = e.target.closest('.cc-cart-page .cart-item__media');
      if (!media) return;
      // Hide alleen als de muis echt buiten de media gaat (niet naar child)
      if (e.relatedTarget && media.contains(e.relatedTarget)) return;
      hidePreview();
    });
    // Verberg ook bij scroll (positie zou stale worden)
    window.addEventListener('scroll', hidePreview, { passive: true });
    root._ccHoverBound = true;
  }

  // ===== Init / re-init =====
  function init() {
    if (!getRoot()) return;
    bindSortUI();
    visibleCount = PAGE_SIZE; // reset bij nieuwe page-load
    applyAllSafe(); // sort + pagination + bind, met observer-loop bescherming
    bindHoverPreview();
    bindNoteSave(); // "Notitie opslaan"-knop (staat in sidebar, buiten .js-contents)
    // Free-shipping bar kleur-update zit nu in assets/cc-shipping-bar.js
    // (globaal, werkt zowel hier als in de cart drawer).
  }

  // Na cart-update (theme cart.js vervangt .js-contents) opnieuw applyen.
  // BELANGRIJK: applySort() doet zelf appendChild() — dat triggert deze
  // observer opnieuw → infinite loop met flikkering van images, qty-inputs
  // en prijzen. We ignoren mutations binnen een korte window na onze eigen
  // DOM-aanpassingen (mutationIgnoreUntil) zodat alleen ECHTE cart-updates
  // (van theme cart.js) re-applyen triggeren.
  let mutationIgnoreUntil = 0;
  function applyAllSafe() {
    mutationIgnoreUntil = Date.now() + 100; // suppress observer trigger uit eigen mutations
    applySort(getSavedSort());
    applyPagination();
    bindPaginationUI();
  }
  function observeCartUpdates() {
    const root = getRoot();
    if (!root || !window.MutationObserver) return;
    const target = root.querySelector('.js-contents');
    if (!target) return;
    let debounce = null;
    new MutationObserver(function () {
      if (Date.now() < mutationIgnoreUntil) return;
      clearTimeout(debounce);
      debounce = setTimeout(applyAllSafe, 30);
    }).observe(target, { childList: true, subtree: true });
  }

  // ===== Notitie opslaan (expliciete knop + checkout-flush) =====
  // De theme's <cart-note> custom element (cart.js) slaat de notitie pas op
  // bij het 'change'-event (= blur) met 300ms debounce. Gecombineerd met
  // onze checkout-intercept hieronder (preventDefault + directe navigatie)
  // wordt die notitie-fetch afgebroken vóór hij de server bereikt → de
  // notitie ging verloren. Daarom hier: (1) een expliciete "Notitie
  // opslaan"-knop met duidelijke feedback, en (2) saveNote() die we vlak
  // vóór checkout gegarandeerd afronden (zie bindCheckoutIntercept).
  function getNoteTextarea() {
    return document.querySelector('.cc-cart-page #Cart-note');
  }
  function getCartUpdateUrl() {
    // routes is een theme-global (window.routes). Fallback voor de zekerheid.
    if (typeof routes !== 'undefined' && routes && routes.cart_update_url) {
      return routes.cart_update_url;
    }
    return '/cart/update.js';
  }
  // POST identiek aan cart.js' <cart-note> handler (zelfde fetchConfig-vorm:
  // POST + JSON content-type, body { note }). Retourneert de fetch-promise.
  function saveNote() {
    const ta = getNoteTextarea();
    if (!ta) return Promise.resolve(null);
    const body = JSON.stringify({ note: ta.value });
    return fetch(getCartUpdateUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: body
    });
  }
  function bindNoteSave() {
    const btn = document.querySelector('.cc-cart-page .cc-cart-note-save');
    if (!btn || btn._ccBound) return;
    const status = document.querySelector('.cc-cart-page .cc-cart-note-status');
    function setStatus(text, state) {
      if (!status) return;
      status.textContent = text;
      status.className = 'cc-cart-note-status' + (state ? ' is-' + state : '');
    }
    btn.addEventListener('click', function () {
      btn.disabled = true;
      setStatus('Bezig met opslaan…', 'saving');
      saveNote()
        .then(function (res) {
          if (!res || !res.ok) throw new Error('cart/update faalde');
          setStatus('Notitie opgeslagen ✓', 'saved');
        })
        .catch(function () {
          setStatus('Opslaan mislukt — probeer opnieuw', 'error');
        })
        .then(function () { btn.disabled = false; });
    });
    // Zodra de klant weer iets typt: status leegmaken (notitie is gewijzigd
    // en dus nog niet opnieuw opgeslagen).
    const ta = getNoteTextarea();
    if (ta && !ta._ccNoteInputBound) {
      ta.addEventListener('input', function () { setStatus('', ''); });
      ta._ccNoteInputBound = true;
    }
    btn._ccBound = true;
  }

  // ===== Checkout intercept — bypass form-submit naar /cart =====
  // Het form posten naar /cart stuurt alle name="updates[]" inputs in
  // DOM-volgorde mee. Sinds wij de cart-items kunnen sorteren in DOM
  // (alfabetisch ipv server-volgorde), zou Shopify die positionele
  // updates op verkeerde line-items toepassen → quantities lopen door
  // elkaar bij checkout. ALLE qty-wijzigingen gaan al via /cart/change
  // (theme cart.js, AJAX) — server-cart is dus altijd up-to-date. We
  // hoeven het form NIET te submitten; we kunnen direct naar /checkout
  // navigeren. De terms-checkbox + disabled-state van de checkout
  // button blijven werken (UI), enkel de POST-submit overslaan we.
  //
  // 350ms wait: theme cart.js debounct qty-change events met 300ms voor
  // ze fetchen naar /cart/change. Als de klant qty wijzigt en direct
  // op "Afrekenen" klikt, kan de pending fetch nog niet zijn gestart
  // OF nog midden in flight zijn. Door 350ms te wachten met de
  // navigatie, geven we die calls de tijd om de server-cart te
  // updaten voor de redirect. Tijdens deze wait krijgt de button
  // .is-loading (spinner + pointer-events:none) als visuele feedback.
  const CHECKOUT_WAIT_MS = 350;
  // Harde fallback: bij een trage/hangende verbinding mag de notitie-flush
  // de klant nooit blokkeren — na deze tijd navigeren we sowieso.
  const CHECKOUT_MAX_WAIT_MS = 2000;
  function bindCheckoutIntercept() {
    const form = document.querySelector('.cc-cart-page form#cart');
    if (!form || form._ccCheckoutIntercepted) return;
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      const btn = form.querySelector('#checkout');
      if (btn) {
        btn.classList.add('is-loading');
        // disabled voorkomt dat ook keyboard-Enter de form opnieuw
        // submit; pointer-events op .is-loading vangt de muis af.
        btn.disabled = true;
      }
      // Navigeer exact één keer, en pas als:
      //  (a) de notitie is geflusht naar de server (saveNote), ÉN
      //  (b) er minstens CHECKOUT_WAIT_MS is verstreken zodat pending
      //      /cart/change qty-updates van theme cart.js (debounce 300ms)
      //      ook zijn afgerond.
      // CHECKOUT_MAX_WAIT_MS dekt het geval dat de flush hangt.
      let navigated = false;
      function go() {
        if (navigated) return;
        navigated = true;
        window.location.href = '/checkout';
      }
      const minWait = new Promise(function (r) { setTimeout(r, CHECKOUT_WAIT_MS); });
      Promise.all([saveNote().catch(function () {}), minWait]).then(go);
      setTimeout(go, CHECKOUT_MAX_WAIT_MS);
    });
    form._ccCheckoutIntercepted = true;
  }

  // ===== /cart/add fetch hook — alleen actief op de /cart pagina =====
  // Theme cart.js werkt netjes voor /cart/change (qty +/− binnen de cart),
  // maar bij /cart/add (bv. via een upsell card op de cart-pagina zelf)
  // wordt de cart-pagina niet vanzelf opnieuw gerenderd: nieuwe items
  // verschijnen niet, totalen blijven oud, free-shipping bar verandert
  // niet. We hooken hier de fetch en doen een korte-delayed reload na
  // een succesvolle /cart/add zodat de hele pagina (en cart-state)
  // opnieuw rendert. Werkt alleen als we OP de /cart pagina zijn —
  // andere pagina's (PDP, card add-to-cart) blijven hun normale flow
  // behouden via theme product.min.js.
  function hookAddToCartReload() {
    if (!getRoot()) return; // alleen op /cart pagina
    const nativeFetch = window.fetch;
    if (!nativeFetch || nativeFetch._ccCartPageAddHooked) return;
    const ADD_RE = /\/cart\/add(\.js)?(\?|$)/;
    const hooked = function (input, init) {
      const url = typeof input === 'string' ? input : input && input.url;
      const isAdd = url && ADD_RE.test(url);
      const promise = nativeFetch.apply(this, arguments);
      if (isAdd) {
        promise.then(function (res) {
          if (res && res.ok) {
            // Korte delay zodat eventuele toast/markRecentAdd nog kan triggeren
            setTimeout(function () { window.location.reload(); }, 500);
          }
        }).catch(function () { /* ignore */ });
      }
      return promise;
    };
    hooked._ccCartPageAddHooked = true;
    window.fetch = hooked;
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
      observeCartUpdates();
      hookAddToCartReload();
      bindCheckoutIntercept();
    });
  } else {
    init();
    observeCartUpdates();
    hookAddToCartReload();
    bindCheckoutIntercept();
  }
  document.addEventListener('shopify:section:load', init);
})();
